import { Trans } from '@lingui/macro'
import { HighlightOff, NavigateNext } from '@mui/icons-material'
import { Box, Button, Grid, IconButton, Tooltip, Typography } from '@mui/material'
import { ButtonIcon } from 'app/views/common-components/ButtonIcon'
import { useFormContext } from '../form-page/FormContext'
import './FormErrorsList.scss'

const GoToErrorButton = ({ error, navigateToError }) => {
  const id = error?.error?.id || error?.id

  return (
    <Tooltip title={<Trans>Go to error</Trans>}>
      <span><IconButton
        onClick={e => {
          navigateToError({
            id,
            section: error.sectionIndex
          })
        }}
            >
        <NavigateNext />
      </IconButton>
      </span>
    </Tooltip>
  )
}

/**
 * Component displaying list of errors currently ocurring in the form.
 * @category Form
 * @component
 * @returns {JSX.Element}
 * @param  {(string|JSX.element)} [helperText] - Help text that will be displaying above errors list.
 * @param {object[]} errors - An array of current Formik errors
 * @param {string} [title] - Text to be used as error message. If not passed, default message is used.
 * @param {Function} navigateToError - Function that navigates user in form to element related to target error.
 */
export const FormErrorsList = ({ errors, helperText, navigateToError, title }) => {
  const { currentStep, sectionsNumber } = useFormContext()

  if (errors.length === 0) {
    return null
  }
  const errorsToSections = {}
  errors.forEach(error => {
    if (errorsToSections[error.sectionName]) {
      errorsToSections[error.sectionName].push(error)
    } else {
      errorsToSections[error.sectionName] = [error]
    }
  })

  const displaySectionTitles = currentStep === sectionsNumber - 1 && sectionsNumber > 1

  return (
    <Grid container sx={{ mt: '24px', mb: '24px' }}>
      <Grid item className='errors-list-strip' sx={{ backgroundColor: 'error.main' }} />

      <Grid wrap='nowrap' item container className='errors-list-container' sx={{ backgroundColor: 'flareSecondary.main' }} gap='12px' xs>

        <Grid item>
          <HighlightOff
            sx={{
              width: '30px',
              height: '30px',
              fontSize: '30px',
              color: '#E51C40'
            }}
          />
        </Grid>

        <Grid item>

          <Typography className='errors-list-header' sx={{ color: 'primary.main' }}>
            {title || (
              <Trans>
                ERRORS_LIST_HEADER
              </Trans>
            )}
          </Typography>

          <Typography className='errors-list-subtitle' sx={{ color: 'primary.main' }} component='div'>
            <Trans>
              ERRORS_LIST_SUBTITLE
            </Trans>
          </Typography>

          <div className='errors-list-error-container'>
            {Object.keys(errorsToSections).map((key, index) => {
              const sectionErrors = errorsToSections[key]
              return (
                <div key={index}>
                  {displaySectionTitles && <Typography className='errors-list-section-label'>{key}</Typography>}
                  <ul>
                    {sectionErrors.map((errorObj, index) => {
                      const { error, title } = errorObj

                      if (error && error.toMap) {
                        return (
                          <li key={index}>
                            <Typography className='errors-list-error-text'>
                              {error?.title || title}
                            </Typography>
                            {error.toMap.map((item, index) => (
                              <Box key={index}>
                                <Button
                                  sx={{ p: 0 }}
                                  onClick={e => {
                                    navigateToError({ section: errorObj.sectionIndex, id: errorObj?.error?.id || errorObj?.id })
                                  }}
                                >
                                  <Grid container direction='row' alignItems='center'>
                                    <Typography className='errors-list-error-text'>
                                      {item}
                                    </Typography>
                                    <Grid item sx={{ paddingLeft: '8px' }}>
                                      <ButtonIcon IconClass={NavigateNext} />
                                    </Grid>
                                  </Grid>
                                </Button>
                              </Box>
                            ))}
                          </li>
                        )
                      } else if (error && typeof error === 'object' && !error.props) {
                        return (
                          <div key={index}>
                            <b>{title}</b>{' '}
                            <GoToErrorButton
                              navigateToError={navigateToError}
                              error={errorObj}
                            />
                            <ul>
                              {Object.keys(error).map((key, index) => (
                                <li key={index}>
                                  <u>{error[key].label}</u>: {error[key].error}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )
                      } else {
                        return (
                          <li key={index}>
                            <Button
                              sx={{ p: 0 }}
                              onClick={e => {
                                navigateToError({ section: errorObj.sectionIndex, id: errorObj?.error?.id || errorObj?.id })
                              }}
                            >
                              <Grid container direction='row' alignContent='center' justifyItems='center' wrap='nowrap'>
                                <Typography sx={{ alignSelf: 'center' }} className='errors-list-error-text'>
                                  {title ? `${title} :` : ''}  {error}
                                </Typography>
                                <ButtonIcon IconClass={NavigateNext} />
                              </Grid>
                            </Button>
                          </li>
                        )
                      }
                    })}
                  </ul>
                </div>
              )
            })}
          </div>

        </Grid>

      </Grid>
    </Grid>
  )
}
